import React, { useState, useContext } from "react";
import { Select, Switch } from "antd";
import { StateContext } from "../../../store/context";
import SLLogo from "../../../icons/srilanka.svg";
import QatarLogo from "../../../icons/qutar.svg";
import UsaLogo from "../../../icons/usa.svg";
import IndiaLogo from "../../../icons/india.svg";
import SaudiLogo from "../../../icons/saudi.svg";
import DubaiLogo from "../../../icons/dubai.svg";

type FormatConfig = {
  decimal: string;
  scale: string;
};

interface ReportFiltersProps {
  selectedPeriod: string;
  onPeriodChange: (value: string) => void;
  viewMode: "quarterly" | "annually";
  onViewModeChange: (checked: boolean) => void;
  periodOptions: { value: string; label: string }[];
  onFormatChange?: (formatConfig: FormatConfig) => void;
  selectedStock?: any;
  children: React.ReactNode;
  reverseDate: boolean;
  onReverseDateChange: (checked: boolean) => void;
  showMargins: boolean;
  onShowMarginsChange: (checked: boolean) => void;
  activeDecimalFormat: string;
  activeScaleFormat: string;
}

export const ReportFilters: React.FC<ReportFiltersProps> = ({
  selectedPeriod,
  onPeriodChange,
  viewMode,
  onViewModeChange,
  periodOptions,
  onFormatChange,
  selectedStock,
  children,
  reverseDate,
  onReverseDateChange,
  showMargins,
  onShowMarginsChange,
  activeDecimalFormat,
  activeScaleFormat,
}) => {
  const { baseCurrency } = useContext(StateContext);

  const marketShortName = selectedStock ? selectedStock?.short_name : "";

  const formatButtonGroups = [
    {
      type: "decimal",
      buttons: [
        { label: ".0", value: "decimal_0" },
        { label: ".00", value: "decimal_00" },
      ],
    },
    {
      type: "scale",
      buttons: [
        { label: "K", value: "thousands" },
        { label: "M", value: "millions" },
        { label: "B", value: "billions" },
      ],
    },
  ];

  const handleFormatButtonClick = (format: string, groupType: string) => {
    if (groupType === "decimal") {
      const newDecimalFormat = activeDecimalFormat === format ? "" : format;
      const formatConfig = {
        decimal: newDecimalFormat,
        scale: activeScaleFormat,
      };
      onFormatChange?.(formatConfig);
    } else if (groupType === "scale") {
      const newScaleFormat = activeScaleFormat === format ? "" : format;
      const formatConfig = {
        decimal: activeDecimalFormat,
        scale: newScaleFormat,
      };
      onFormatChange?.(formatConfig);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <Select
            value={selectedPeriod}
            onChange={onPeriodChange}
            style={{ width: 150 }}
            options={periodOptions}
            popupClassName="vi-fin-stock-popup"
          />

          <div className="flex items-center gap-3">
            {formatButtonGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="flex items-center">
                {group.buttons.map((button, buttonIndex) => {
                  const isActive =
                    group.type === "decimal"
                      ? activeDecimalFormat === button.value
                      : activeScaleFormat === button.value;
                  return (
                    <button
                      key={button.value}
                      onClick={() =>
                        handleFormatButtonClick(button.value, group.type)
                      }
                      className={`
                        px-3 py-1.5 text-sm font-medium border transition-colors duration-200
                        ${buttonIndex === 0 ? "rounded-l-md" : ""}
                        ${
                          buttonIndex === group.buttons.length - 1
                            ? "rounded-r-md"
                            : ""
                        }
                        ${buttonIndex > 0 ? "border-l-0" : ""}
                        ${
                          isActive
                            ? "bg-gray-300/60 text-gray-700 border-gray-300/60 z-10 relative"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }
                      `}
                    >
                      {button.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-700">Reverse Dates</span>
            <Switch checked={reverseDate} onChange={onReverseDateChange} />
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-700">Show Margins</span>
            <Switch checked={showMargins} onChange={onShowMarginsChange} />
          </div>

          <div className="flex items-center gap-x-2">
            <span className="text-[#344054] font-dm text-sm">CRY:</span>
            <img
              src={
                baseCurrency === "LKR" && marketShortName === "CSE"
                  ? SLLogo
                  : baseCurrency === "QAR" && marketShortName === "QSE"
                    ? QatarLogo
                    : baseCurrency === "USD" && marketShortName === "NYSE"
                      ? UsaLogo
                      : baseCurrency === "INR" && marketShortName === "ISE"
                        ? IndiaLogo
                        : baseCurrency === "AED" && marketShortName === "DFM"
                          ? DubaiLogo
                          : baseCurrency === "SAR" && marketShortName === "SSE"
                            ? SaudiLogo
                            : baseCurrency === "AED" &&
                                marketShortName === "ADX"
                              ? DubaiLogo
                              : SLLogo
              }
              alt=""
              className="w-8 h-6 object-cover"
            />
            <span className="text-[#344054] font-dm text-sm">
              {baseCurrency}
            </span>
          </div>
        </div>

        {/* Right side - View Mode Toggle */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-700">Quarterly</span>
          <Switch
            checked={viewMode === "annually"}
            onChange={onViewModeChange}
          />
          <span className="text-sm text-gray-700">Annually</span>
        </div>
      </div>
      {children}
    </>
  );
};
